import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { ArrowUpRight, BookOpen, MessageCircle, FileQuestion, Languages, BarChart3, Upload, Sparkles, ShieldCheck, X, Check, Send, Lock, EyeOff, Scale, Calculator, Users, TrendingDown, Clock, MessagesSquare, AlertTriangle, Smartphone, Minus, Plus, IndianRupee, CreditCard } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { startRazorpaySubscription } from "@/lib/razorpay";
import { toast } from "sonner";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/a6f92815-ad09-4e5f-b145-5f86fe09afd8/images/8cd49324e62e47da3fb9d852c9cb651b3339a3dd18fdad3f3fce0778674a2929.png";
const FEATURE_3D = "/logo.png";
const BOOKS_BG = "https://images.unsplash.com/photo-1495446815901-a7297e633e8d?auto=format&fit=crop&w=900&q=80";
const STUDENT_IMG = "/landing-students.jpg";
const HERO_BRANDED = "/landing-hero-branded.png";  // New: VidyaAI Study logo on books backdrop (uploaded by user)

const LANG_ROTATOR = ["Hindi", "Marathi", "Telugu", "Hinglish", "Punjabi", "Tamil", "Bengali", "Gujarati", "Kannada", "Malayalam"];

const features = [
  { icon: MessageCircle, title: "Doubt solving in your language", desc: "Students ask in English, Hindi, Marathi, Tamil, Bengali and more — the tutor responds in kind, grounded only in your materials." },
  { icon: BookOpen, title: "Chapter summaries & revision notes", desc: "Turn long PDFs and lecture notes into crisp summaries, key formulas, and flashcards in seconds." },
  { icon: FileQuestion, title: "Quiz & MCQ generation", desc: "Auto-generate practice tests with explanations from any chapter — by difficulty and language." },
  { icon: Upload, title: "Your private content library", desc: "Upload PDFs, handwritten notes (OCR'd) and video lectures. The AI tutor answers strictly from your institute's syllabus." },
  { icon: Languages, title: "Built for Bharat", desc: "First-class multilingual support for Indian regional languages and code-mixed conversations." },
  { icon: BarChart3, title: "Telegram + Web in one tutor", desc: "Students open one invite link, tap Start, and chat with the same AI tutor on Telegram — or on the web. Class broadcasts too." },
];

export default function Landing() {
  const [langIdx, setLangIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setLangIdx((i) => (i + 1) % LANG_ROTATOR.length), 1800);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="landing-page">
      <Seo
        title="AI Tutor for Coaching Institutes & Self-Study Students | VidyaAI Study"
        description="Build your own AI tutor from uploaded notes, PDFs, videos and YouTube lectures. Answers in Hindi, Tamil, Telugu, Marathi, Bengali, Gujarati & 10+ Indian languages. Works on Telegram + Web."
        path="/"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "VidyaAI Study",
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web, Telegram",
            "description": "VidyaAI Study lets coaching institutes and self-study students build their own AI tutor from uploaded notes, PDFs, videos and YouTube links. Instant doubt solving, quizzes and summaries in 10+ Indian languages.",
            "url": "https://vidyaai.study/",
            "image": "https://vidyaai.study/logo.png",
            "inLanguage": ["en", "hi", "mr", "ta", "te", "kn", "bn", "gu", "ml", "pa"],
            // `offers` is required by Google for SoftwareApplication rich-results.
            // Multiple Offer entries advertise both the free tier and the paid Plus / Pro / Growth tiers.
            "offers": [
              { "@type": "Offer", "name": "Self-Study Free",  "price": "0",   "priceCurrency": "INR", "category": "free",      "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "Self-Study Plus",  "price": "99",  "priceCurrency": "INR", "category": "subscription", "billingDuration": "P1M", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "Self-Study Pro",   "price": "199", "priceCurrency": "INR", "category": "subscription", "billingDuration": "P1M", "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "Institute Starter","price": "0",   "priceCurrency": "INR", "category": "free",      "availability": "https://schema.org/InStock" },
              { "@type": "Offer", "name": "Institute Growth", "price": "30",  "priceCurrency": "INR", "category": "subscription", "billingDuration": "P1M", "unitText": "per seat per month", "availability": "https://schema.org/InStock" }
            ],
            "publisher": {
              "@type": "Organization",
              "name": "Sunmagma Technologies LLP",
              "url": "https://vidyaai.study/",
              "logo": "https://vidyaai.study/logo.png",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Unit No 2, 4th Floor, A Wing, Time Square Building, Andheri Kurla Road, Marol, Nilkanth Park",
                "addressLocality": "Andheri (E), Mumbai",
                "postalCode": "400059",
                "addressCountry": "IN"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "info@vidyaai.study",
                "telephone": "+91-8591609998",
                "contactType": "customer support",
                "areaServed": "IN"
              }
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "What is VidyaAI Study?", "acceptedAnswer": { "@type": "Answer", "text": "VidyaAI Study is an AI tutor platform that lets coaching institutes and self-study students upload notes, PDFs, audio and video lectures, and chat with an AI tutor that answers strictly from that material — in English, Hindi, Marathi, Tamil, Telugu, Kannada, Malayalam, Bengali, Gujarati and Punjabi." } },
              { "@type": "Question", "name": "Does the AI tutor work on Telegram?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Students tap one invite link, share their phone for verification, and chat with the same AI tutor on Telegram — answers grounded only in the institute's uploaded materials." } },
              { "@type": "Question", "name": "Which exams does VidyaAI Study cover?", "acceptedAnswer": { "@type": "Answer", "text": "Every Indian exam — JEE Main/Advanced, NEET UG/PG, CUET, UPSC CSE, State PSCs, GATE, CAT, CLAT, Banking (IBPS/SBI/RBI/SEBI), SSC, CBSE/ICSE/State Boards, plus international exams (SAT, ACT, IELTS, GRE, GMAT, IB, A-Levels)." } },
              { "@type": "Question", "name": "How does the AI tutor avoid hallucinations?", "acceptedAnswer": { "@type": "Answer", "text": "We use Hybrid RAG (BM25 + Gemini embeddings) to retrieve the most relevant chunks from your uploaded notes, and the tutor is instructed to refuse any question not covered in your material — with a one-tap option to switch to general knowledge if you allow it." } }
            ]
          }
        ]}
      />
      <Navbar variant="marketing" />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-90"
          style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          aria-hidden
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#FDFBF7]/30 via-[#FDFBF7]/60 to-[#FDFBF7]" aria-hidden />
        <div className="container-x pt-8 sm:pt-10 lg:pt-12 pb-12 sm:pb-16">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-7 animate-fadeUp">
              {/* Top eyebrow — matches the original image_8ca73f.png aesthetic */}
              <div className="label-eyebrow mb-5" data-testid="hero-eyebrow">
                AI TUTOR ON TELEGRAM &amp; WEB • FROM YOUR NOTES • MADE IN INDIA
              </div>

              {/* Three-line slogan with red highlight on the key nouns */}
              <h1
                className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] sm:leading-[0.95] font-medium text-[#1A1713]"
                data-testid="hero-h1"
              >
                Your <span className="text-[#C24933]">Notes.</span>
                <span className="block">Your <span className="text-[#C24933]">Language.</span></span>
                Your <span className="text-[#C24933]">AI Tutor.</span>
                <span className="block mt-2 sm:mt-3 text-2xl sm:text-3xl lg:text-4xl text-[#4A4640] font-normal tracking-tight">
                  Learning in{" "}
                  <span
                    key={LANG_ROTATOR[langIdx]}
                    className="text-[#C24933] inline-block animate-fadeUp"
                    aria-live="polite"
                    data-testid="hero-lang-rotator"
                  >
                    {LANG_ROTATOR[langIdx]}
                  </span>
                </span>
              </h1>

              {/* Paragraph description — encouraging, growth-framed, jargon-free */}
              <p className="mt-6 sm:mt-7 text-base sm:text-lg text-[#4A4640] max-w-2xl leading-relaxed" data-testid="hero-subhead">
                Upload class notes, chapter PDFs, video lecture links, or audio to create a helpful, private digital tutor. It answers questions strictly from your verified study materials with zero outside distraction. Multilingual, running beautifully inside Telegram.
              </p>

              {/* Positioning line — sits between subhead and Telegram tagline pill */}
              <p className="mt-4 text-sm text-[#7A7369] italic max-w-2xl" data-testid="hero-teacher-promise">
                We don&apos;t replace teachers. We make teachers&apos; time worth more.
              </p>

              {/* Light blue Telegram quick-action pill — matches image_8ca73f.png */}
              <a
                href="#how"
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#229ED9]/10 border border-[#229ED9]/30 text-[#229ED9] text-xs sm:text-sm font-medium hover:bg-[#229ED9]/15 transition-colors"
                data-testid="hero-telegram-badge"
              >
                <Send size={14} strokeWidth={2} className="rotate-[-15deg]" />
                <span>Doubts solved straight on Telegram — bina app khole, batch group mein bhi.</span>
              </a>

              <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <Link to="/register?role=self_study_student" className="btn-primary" data-testid="hero-cta-trial">
                  Start Free <ArrowUpRight size={16} strokeWidth={2} />
                </Link>
                <Link to="/demo" className="btn-ghost" data-testid="hero-cta-demo">
                  See a Demo
                </Link>
                <Link
                  to="/contact"
                  className="btn-ghost"
                  data-testid="hero-cta-contact"
                >
                  Contact Us
                </Link>
              </div>
              <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#7A7369]">
                <span>Strict source-grounded answers</span>
                <span className="hidden sm:inline">·</span>
                <span>10+ Indian languages</span>
                <span className="hidden sm:inline">·</span>
                <span>No coding needed</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative pb-20 pr-2 sm:pr-4">
                {/* Main branded card — VidyaAI Study logo on books backdrop (uploaded by user) */}
                <div className="relative w-full aspect-[4/5] rounded-3xl shadow-xl overflow-hidden">
                  <img
                    src={HERO_BRANDED}
                    alt="VidyaAI Study — your notes, your language, your AI tutor"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="hero-brand-image"
                  />
                </div>

                {/* Telegram-style mobile chat mock — overlay bottom-right */}
                <div
                  className="absolute -bottom-2 -right-2 sm:-right-4 w-[230px] sm:w-[270px] rounded-[28px] bg-[#1A1713] p-2 shadow-2xl rotate-[3deg]"
                  data-testid="hero-telegram-mock"
                  aria-label="Telegram chat with VidyaAI Study"
                >
                  <div className="rounded-[22px] bg-[#FDFBF7] overflow-hidden">
                    {/* TG header */}
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-[#2BA3E1] text-white">
                      <div className="w-7 h-7 rounded-full bg-white grid place-items-center">
                        <img src="/logo-mark.png" alt="" className="w-5 h-5 object-contain" />
                      </div>
                      <div className="leading-tight min-w-0">
                        <div className="text-xs font-medium truncate">VidyaAI Study Bot</div>
                        <div className="text-[10px] text-white/80">online · NEET tutor</div>
                      </div>
                      <Send size={12} className="ml-auto text-white/80" />
                    </div>
                    {/* TG messages */}
                    <div className="px-3 py-3 space-y-2 bg-[#EAE5D8]">
                      <div className="flex justify-end">
                        <div className="bg-[#DCF8C6] text-[#1A1713] text-[11px] leading-snug px-2.5 py-1.5 rounded-2xl rounded-br-md max-w-[78%]">
                          Newton&apos;s 2nd law samjhao 🙏
                        </div>
                      </div>
                      <div className="flex justify-start">
                        <div className="bg-white text-[#1A1713] text-[11px] leading-snug px-2.5 py-2 rounded-2xl rounded-bl-md max-w-[88%] space-y-1.5">
                          <div>Newton&apos;s second law कहता है ki <strong>force = mass × acceleration</strong>.</div>
                          <div className="text-[10px] text-[#7A7369] flex items-center gap-1">
                            <BookOpen size={10} className="text-[#C24933]" />
                            <span>Source · Ch.4 · p.42</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 pt-1">
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/80 border border-[#E6E2D8] text-[#4A4640]">💡 Hint</span>
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-white/80 border border-[#E6E2D8] text-[#4A4640]">📋 Summary</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR — E4 "Unlike generic AI" callout */}
      <section className="container-x pt-4 sm:pt-6 pb-10 sm:pb-14" data-testid="differentiator-section">
        <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-10">
          <div className="label-eyebrow mb-4">Why VidyaAI Study, not ChatGPT?</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight mb-4">
            Built <span className="text-[#C24933]">FROM your notes</span> — not from the internet.
          </h2>
          <p className="text-base sm:text-lg text-[#4A4640] leading-relaxed">
            Generic AI hallucinates. We refuse. Generic AI gives the same answer in every language. We mirror your dialect.
            Every reply you see is tied to a page of <strong>your</strong> uploaded study material — not a global model&apos;s guess.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
          {/* Generic AI column */}
          <div className="card-surface !bg-[#F4F1EB] !border-[#E6E2D8]" data-testid="differentiator-generic">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-[#E6E2D8] grid place-items-center"><MessageCircle size={16} className="text-[#7A7369]" strokeWidth={1.6} /></div>
              <div className="font-display text-lg tracking-tight text-[#7A7369]">Generic AI</div>
            </div>
            <ul className="space-y-3 text-sm text-[#4A4640]">
              {[
                "Answers from the entire internet",
                "Hallucinates exam-irrelevant content",
                "Same boilerplate reply in any language",
                "No exam-specific calibration",
                "No source — you can't verify the answer",
              ].map((line, i) => (
                <li key={`con-${i}-${(line || '').slice(0,24)}`} className="flex items-start gap-2.5"><X size={15} className="mt-0.5 shrink-0 text-[#B73B3B]" strokeWidth={2} /><span>{line}</span></li>
              ))}
            </ul>
          </div>

          {/* VidyaAI Study column */}
          <div className="card-surface !border-[#C24933] !bg-white relative" data-testid="differentiator-vidyaai">
            <div className="absolute -top-3 right-5 bg-[#C24933] text-white text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">VidyaAI Study</div>
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-[#FCE8E2] grid place-items-center"><ShieldCheck size={16} className="text-[#C24933]" strokeWidth={1.6} /></div>
              <div className="font-display text-lg tracking-tight">Your private AI tutor</div>
            </div>
            <ul className="space-y-3 text-sm text-[#1A1713]">
              {[
                "Answers ONLY from YOUR uploaded notes",
                "Refuses honestly when topic isn't in your material",
                "Mirrors your dialect — Hinglish, Tamil, Marathi, more",
                "Calibrates depth for NEET, JEE, UPSC, Boards",
                "Every answer cites the exact page",
              ].map((line, i) => (
                <li key={`pro-${i}-${(line || '').slice(0,24)}`} className="flex items-start gap-2.5"><Check size={15} className="mt-0.5 shrink-0 text-[#4A6B53]" strokeWidth={2.5} /><span>{line}</span></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="text-center mt-10">
          <Link to="/demo" className="btn-primary" data-testid="differentiator-cta-demo">
            Try the difference — no signup <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* WIN-WIN CO-GROWTH — Teachers + Students side-by-side. Positive, growth-focused. */}
      <WinWinSection />

      {/* FEATURES */}
      <section id="features" className="container-x py-10 sm:py-14">
        <div className="grid md:grid-cols-12 gap-10 mb-10">
          <div className="md:col-span-5">
            <div className="label-eyebrow mb-4">What it does</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight leading-tight">Everything a smart tutor would — but private to your institute.</h2>
          </div>
          <div className="md:col-span-7 md:pt-2 text-[#4A4640] leading-relaxed">
            Most &ldquo;AI tutors&rdquo; hallucinate. VidyaAI Study is grounded only in the books, notes and lectures you upload — so answers always match your curriculum. Branded to your institute. Available to your students 24×7.
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={`feat-${i}-${(f?.title || '').slice(0,20)}`} className="card-surface hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#F4F1EB] flex items-center justify-center mb-5">
                  <Icon size={20} strokeWidth={1.6} className="text-[#C24933]" />
                </div>
                <h3 className="font-display text-xl tracking-tight mb-2">{f.title}</h3>
                <p className="text-sm text-[#4A4640] leading-relaxed">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* BATCH ACCELERATION — encouraging slider, no numeric ROI; pure growth framing. */}
      <BatchAccelerationSection />

      {/* HOW */}
      <section id="how" className="bg-[#F4F1EB] py-10 sm:py-14">
        <div className="container-x grid lg:grid-cols-12 gap-8 sm:gap-12 items-center">
          <div className="lg:col-span-5">
            <img src={STUDENT_IMG} alt="" loading="lazy" className="rounded-3xl w-full object-cover h-72 sm:h-[440px]" />
          </div>
          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-4">How it works</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight leading-tight mb-8">From your bookshelf to your students — in three steps.</h2>
            <ol className="space-y-6">
              {[
                { n: "01", t: "Upload your materials", d: "PDFs, notes, lecture transcripts, question banks. Organise by subject and chapter." },
                { n: "02", t: "Invite your students", d: "Share your institute's join code. Students sign up and get instant access to the AI tutor." },
                { n: "03", t: "Watch them learn", d: "Doubts solved 24×7, quizzes generated on demand, and an analytics dashboard for you." },
              ].map((s) => (
                <li key={s.n} className="flex gap-6">
                  <div className="font-display text-2xl text-[#C24933] font-medium w-14 shrink-0">{s.n}</div>
                  <div>
                    <div className="font-display text-xl tracking-tight mb-1">{s.t}</div>
                    <p className="text-[#4A4640] leading-relaxed">{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* DATA SECURITY & IP TRUST BANNER — placed right before PLANS */}
      <SecurityTrustBanner />

      {/* OUR PROMISE — commitments to institutes about what VidyaAI Study will never do */}
      <OurPromiseSection />

      {/* PRICING — Phase 56: dual-mode pricing cards (Student / Institute) with seat counter + Razorpay. */}
      <PricingSection />


      {/* CTA */}
      <section className="container-x pb-10 sm:pb-16">
        <div className="rounded-3xl bg-[#1A1713] text-[#FDFBF7] p-8 sm:p-12 md:p-16 flex flex-col md:flex-row md:items-end justify-between gap-6 sm:gap-8">
          <div className="max-w-xl">
            <div className="label-eyebrow mb-3" style={{ color: "#E6E2D8" }}>Ready when you are</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight">Give every student a personal tutor — in their language.</h2>
          </div>
          <Link to="/register" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#C24933] text-[#FDFBF7] hover:bg-[#A83F2B] transition-colors text-sm font-medium" data-testid="footer-cta">
            Create your institute <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E6E2D8] bg-[#FDFBF7]">
        <div className="container-x py-12">
          <div className="grid md:grid-cols-4 gap-8 text-sm">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo-mark.png" alt="VidyaAI Study" className="w-8 h-8 rounded-lg object-contain" />
                <span className="font-display text-lg">VidyaAI Study</span>
              </div>
              <p className="text-[#7A7369] leading-relaxed">An AI tutor built from your notes, PDFs and videos — for Indian coaching institutes and self-study students.</p>
              <p className="text-[11px] text-[#7A7369] mt-3">A service by <a href="/about" className="hover:text-[#C24933]">Sunmagma Technologies LLP</a></p>
            </div>
            <div>
              <div className="label-eyebrow mb-3">Product</div>
              <ul className="space-y-2 text-[#4A4640]">
                <li><Link to="/register" className="hover:text-[#C24933]">Sign up</Link></li>
                <li><Link to="/login" className="hover:text-[#C24933]">Sign in</Link></li>
                <li><Link to="/register?role=self_study_student" className="hover:text-[#C24933]">Self-study (free forever)</Link></li>
                <li><Link to="/blog" className="hover:text-[#C24933]" data-testid="footer-blog">Blog</Link></li>
              </ul>
            </div>
            <div>
              <div className="label-eyebrow mb-3">Legal</div>
              <ul className="space-y-2 text-[#4A4640]">
                <li><Link to="/privacy" className="hover:text-[#C24933]" data-testid="legal-privacy">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#C24933]" data-testid="legal-terms">Terms of Service</Link></li>
                <li><Link to="/refund-policy" className="hover:text-[#C24933]" data-testid="legal-refund">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy" className="hover:text-[#C24933]" data-testid="legal-cancellation">Cancellation Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-[#C24933]" data-testid="legal-cookies">Cookie Policy</Link></li>
                <li><Link to="/grievance" className="hover:text-[#C24933]" data-testid="legal-grievance">Grievance Redressal</Link></li>
              </ul>
            </div>
            <div>
              <div className="label-eyebrow mb-3">Support</div>
              <ul className="space-y-2 text-[#4A4640]">
                <li><a href="mailto:info@vidyaai.study" className="hover:text-[#C24933]">info@vidyaai.study</a></li>
                <li><a href="https://api.whatsapp.com/send?phone=918591609998" target="_blank" rel="noreferrer" className="hover:text-[#C24933]">WhatsApp +91 85916 09998</a></li>
                <li><a href="https://t.me/+918591609998" target="_blank" rel="noreferrer" className="hover:text-[#C24933]">Telegram +91 85916 09998</a></li>
                <li><Link to="/contact" className="hover:text-[#C24933]">Contact us</Link></li>
                <li><Link to="/about" className="hover:text-[#C24933]" data-testid="footer-about-link">About us</Link></li>
                <li><Link to="/blog" className="hover:text-[#C24933]" data-testid="footer-blog-link">Blog</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-[#E6E2D8] flex flex-wrap items-center justify-between gap-2 text-xs text-[#7A7369]">
            <div>© {new Date().getFullYear()} Sunmagma Technologies LLP · VidyaAI Study · Made in India 🇮🇳</div>
            <div>Compliant with DPDP Act 2023, IT Act 2000 & Consumer Protection Act 2019</div>
          </div>
        </div>
      </footer>
    </div>
  );
}


// ------------------------------------------------------------------
// Win-Win Co-Growth Section — "One Powerful System. Two Incredible Benefits."
// ------------------------------------------------------------------
// Two-column side-by-side: Teachers (Multiply Your Teaching Impact) on the
// left, Students (Learn Smarter, Score Higher) on the right. Tone is
// purely positive, growth-framed, and free of operational-pain language.
// Each column has a distinct accent (institute = warm gold, student = soft
// teal) so they feel like equal partners rather than one outranking the
// other.
function WinWinSection() {
  const teacherPoints = [
    {
      title: "24/7 Teaching Assistant",
      copy: "Your printed booklets and notes come alive, giving your students instant help even late at night when you are resting.",
    },
    {
      title: "Boost Your Brand Value",
      copy: "Give your institute its own custom-branded Telegram bot. For your students, it feels like a premium digital facility built just for them.",
    },
    {
      title: "Complete Content Safety",
      copy: "Your hard-earned notes are entirely locked to your batch. Your intellectual property stays 100% private.",
    },
  ];
  const studentPoints = [
    {
      title: "100% Focused on Your Syllabus",
      copy: "No generic, confusing internet answers. The bot replies only using the exact notes taught by your teacher.",
    },
    {
      title: "Instant Page Citations",
      copy: "Every answer includes the exact chapter and page number of your class book, so you can open your physical book and verify it instantly.",
    },
    {
      title: "Talk Like a Friend",
      copy: "No need to type perfect English. Ask questions comfortably in your native tongue or mixed speech (Hinglish/Marathi text).",
    },
  ];
  return (
    <section className="container-x py-12 sm:py-16" data-testid="winwin-section">
      <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
        <div className="label-eyebrow mb-4">Built for everyone in the classroom</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight">
          One Powerful System.{" "}
          <span className="text-[#C24933]">Two Incredible Benefits.</span>
        </h2>
        <p className="mt-4 text-base sm:text-lg text-[#4A4640] leading-relaxed">
          When teachers grow, students grow. VidyaAI Study is designed so both sides win at the same time.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 sm:gap-6 max-w-5xl mx-auto">
        {/* TEACHERS column — warm gold accent */}
        <div
          className="card-surface !p-7 sm:!p-8 !border-[#E5A93A] !bg-[#FFF7E6] flex flex-col"
          data-testid="winwin-teachers-column"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl" aria-hidden>✨</span>
            <div className="label-eyebrow" style={{ color: "#7A5A14" }}>For Coaching Centers &amp; Teachers</div>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl tracking-tight mb-6 text-[#1A1713]" data-testid="winwin-teachers-header">
            Multiply Your Teaching Impact
          </h3>
          <ul className="space-y-5">
            {teacherPoints.map((p, i) => (
              <li key={p.title} className="flex gap-4">
                <div className="shrink-0 w-9 h-9 rounded-full bg-[#E5A93A] text-[#1A1713] font-display text-sm font-semibold grid place-items-center">
                  {i + 1}
                </div>
                <div>
                  <div className="font-display text-base sm:text-lg tracking-tight mb-1 text-[#1A1713]">
                    {p.title}
                  </div>
                  <p className="text-sm text-[#4A4640] leading-relaxed">{p.copy}</p>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/register" className="mt-7 btn-ghost self-start" data-testid="winwin-teachers-cta">
            Open Your Institute Account <ArrowUpRight size={14} />
          </Link>
        </div>

        {/* STUDENTS column — soft teal accent */}
        <div
          className="card-surface !p-7 sm:!p-8 !border-[#4A6B53] !bg-[#E6EEE7] flex flex-col"
          data-testid="winwin-students-column"
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl" aria-hidden>🚀</span>
            <div className="label-eyebrow" style={{ color: "#3D5844" }}>For Self-Study &amp; Batch Students</div>
          </div>
          <h3 className="font-display text-2xl sm:text-3xl tracking-tight mb-6 text-[#1A1713]" data-testid="winwin-students-header">
            Learn Smarter, Score Higher
          </h3>
          <ul className="space-y-5">
            {studentPoints.map((p, i) => (
              <li key={p.title} className="flex gap-4">
                <div className="shrink-0 w-9 h-9 rounded-full bg-[#4A6B53] text-white font-display text-sm font-semibold grid place-items-center">
                  {i + 1}
                </div>
                <div>
                  <div className="font-display text-base sm:text-lg tracking-tight mb-1 text-[#1A1713]">
                    {p.title}
                  </div>
                  <p className="text-sm text-[#4A4640] leading-relaxed">{p.copy}</p>
                </div>
              </li>
            ))}
          </ul>
          <Link to="/register?role=self_study_student" className="mt-7 btn-ghost self-start" data-testid="winwin-students-cta">
            Start Self-Study Free <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}


// ------------------------------------------------------------------
// Batch Acceleration Slider — "See Your Batch Learning Efficiency Grow"
// ------------------------------------------------------------------
// Pure growth/encouragement framing. Slider 50–1,000 students. No numeric
// ROI calculation — instead two static reassurance counters that highlight
// 24/7 doubt resolution and continuous active recall. Visual feedback only:
// as the slider moves, the student-count number flexes for the owner to
// see "their batch" reflected on screen.
function BatchAccelerationSection() {
  const [students, setStudents] = useState(250);
  return (
    <section
      id="batch-acceleration"
      className="container-x py-12 sm:py-16"
      data-testid="batch-acceleration-section"
    >
      <div className="max-w-5xl mx-auto">
        <div className="card-surface !p-8 sm:!p-10 lg:!p-12 !border-[#1A1713] !bg-[#FDFBF7] relative overflow-hidden">
          {/* Decorative warm corner glow */}
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#C24933]/8 blur-2xl pointer-events-none" aria-hidden />
          <div className="relative">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <div className="label-eyebrow mb-3">Watch your batch thrive</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tighter leading-tight">
                See Your Batch Learning{" "}
                <span className="text-[#C24933]">Efficiency Grow</span>
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#4A4640] leading-relaxed">
                Pick the size of the batch you teach. Watch how every single student gets the same instant attention you wish you could give them yourself.
              </p>
            </div>

            {/* Slider */}
            <div className="mb-8">
              <div className="flex items-baseline justify-between mb-3">
                <label htmlFor="batch-students" className="text-xs uppercase tracking-widest font-semibold text-[#7A7369]">
                  <Users size={12} className="inline -mt-0.5 mr-1.5" /> Active Student Batch Size
                </label>
                <div
                  className="font-display text-3xl sm:text-4xl text-[#C24933] tabular-nums tracking-tight"
                  data-testid="batch-student-count"
                >
                  {students.toLocaleString("en-IN")}
                </div>
              </div>
              <input
                id="batch-students"
                type="range"
                min={50}
                max={1000}
                step={50}
                value={students}
                onChange={(e) => setStudents(parseInt(e.target.value, 10))}
                className="w-full h-2 rounded-full appearance-none bg-[#E6E2D8] outline-none accent-[#C24933] cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#C24933]
                  [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white
                  [&::-webkit-slider-thumb]:shadow-md
                  [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full
                  [&::-moz-range-thumb]:bg-[#C24933] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white"
                data-testid="batch-slider"
                aria-valuemin={50}
                aria-valuemax={1000}
                aria-valuenow={students}
                aria-label="Active student batch size"
              />
              <div className="flex justify-between text-[10px] text-[#7A7369] mt-2 font-medium tracking-wide">
                <span>50</span>
                <span>250</span>
                <span>500</span>
                <span>750</span>
                <span>1,000</span>
              </div>
            </div>

            {/* Two encouragement counters */}
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mb-8">
              <div
                className="rounded-2xl border-2 border-[#4A6B53] bg-[#E6EEE7] p-5 sm:p-6 flex items-start gap-3"
                data-testid="batch-counter-doubt"
              >
                <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shrink-0">
                  <Clock size={20} strokeWidth={1.7} className="text-[#3D5844]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-[#3D5844] mb-1">
                    Instant Doubt Resolution Capacity
                  </div>
                  <div className="font-display text-xl sm:text-2xl text-[#1A1713] tracking-tight leading-snug">
                    24/7 Continuous Support Available
                  </div>
                  <p className="text-xs text-[#4A4640] mt-2 leading-relaxed">
                    Every student in your batch gets answers the moment they ask — not next class.
                  </p>
                </div>
              </div>
              <div
                className="rounded-2xl border-2 border-[#C24933] bg-[#FCEFEC] p-5 sm:p-6 flex items-start gap-3"
                data-testid="batch-counter-recall"
              >
                <div className="w-10 h-10 rounded-xl bg-white grid place-items-center shrink-0">
                  <TrendingDown size={20} strokeWidth={1.7} className="text-[#7A2F23] rotate-180" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-semibold text-[#7A2F23] mb-1">
                    Student Exam Confidence Booster
                  </div>
                  <div className="font-display text-xl sm:text-2xl text-[#1A1713] tracking-tight leading-snug">
                    Continuous active recall directly inside Telegram
                  </div>
                  <p className="text-xs text-[#4A4640] mt-2 leading-relaxed">
                    Quick quizzes, summaries, and revision prompts keep your material alive between classes.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/register"
                className="btn-primary w-full sm:w-auto justify-center"
                data-testid="batch-cta-trial"
              >
                Launch Your Free Starter Plan <ArrowUpRight size={16} strokeWidth={2} />
              </Link>
              <span className="text-xs text-[#7A7369]">
                No credit card · Brand it with your institute&apos;s name
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}




// ------------------------------------------------------------------
// Data Security & IP Trust Banner (placed right before pricing/plans)
// ------------------------------------------------------------------
function SecurityTrustBanner() {
  const pillars = [
    {
      icon: Lock,
      title: "Notes Locked to Your Bot",
      copy: "Your printed modules and notes are securely tied to your specific Telegram Bot. No outside student or competitor can ever see them.",
      tone: "#1A1713",
      bg: "#F4F1EB",
      testId: "security-pillar-isolation",
    },
    {
      icon: EyeOff,
      title: "Never Shared. Ever.",
      copy: "Your booklets are never used to train any global system. Your hard work stays your hard work.",
      tone: "#7A2F23",
      bg: "#FCEFEC",
      testId: "security-pillar-no-training",
    },
    {
      icon: Scale,
      title: "Built In India",
      copy: "Fully compliant with India's DPDP Act 2023, IT Act 2000, and Consumer Protection Act 2019. Made for Indian institutes.",
      tone: "#3D5844",
      bg: "#E6EEE7",
      testId: "security-pillar-compliance",
    },
  ];
  return (
    <section
      className="bg-[#FDFBF7] py-12 sm:py-16 border-y border-[#E6E2D8]"
      data-testid="security-trust-banner"
    >
      <div className="container-x">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 label-eyebrow mb-4">
            <ShieldCheck size={12} strokeWidth={2} className="text-[#C24933]" />
            Your IP, locked
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight">
            Your Material is <span className="text-[#C24933]">100% Locked &amp; Protected</span>
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#4A4640] leading-relaxed">
            We know your printed study modules are your top asset. Your notes are securely locked to your specific Telegram Bot. No outside student, competitor, or global system can ever see or copy your intellectual property.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="card-surface !p-7 hover:-translate-y-1 hover:shadow-lg transition-all"
                data-testid={p.testId}
              >
                <div
                  className="w-11 h-11 rounded-xl grid place-items-center mb-5"
                  style={{ backgroundColor: p.bg }}
                >
                  <Icon size={20} strokeWidth={1.7} style={{ color: p.tone }} />
                </div>
                <h3 className="font-display text-xl tracking-tight mb-2 text-[#1A1713]">{p.title}</h3>
                <p className="text-sm text-[#4A4640] leading-relaxed">{p.copy}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// ------------------------------------------------------------------
// OUR PROMISE — commitments to institutes about what VidyaAI Study
// will NEVER do with their content. Sits between the IP trust banner
// and the pricing section. Deliberately quiet visual weight — small
// eyebrow label, tight bullet list — the message does the work.
// ------------------------------------------------------------------
function OurPromiseSection() {
  const promises = [
    "Sell your notes or study material to anyone — ever.",
    "Use your content to train any AI model.",
    "Share your material with any other institute or competitor.",
    "Allow any student outside your batch to access your content.",
  ];
  return (
    <section
      className="container-x py-12 sm:py-16"
      data-testid="our-promise-section"
    >
      <div className="max-w-3xl mx-auto text-center">
        <div className="label-eyebrow mb-4">Our promise</div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight">
          What We Will <span className="text-[#C24933]">Never Do</span>
        </h2>
      </div>

      <ul className="mt-8 sm:mt-10 max-w-2xl mx-auto space-y-4" data-testid="our-promise-list">
        {promises.map((line, i) => (
          <li
            key={i}
            className="flex items-start gap-3 text-base sm:text-lg text-[#1A1713] leading-relaxed"
            data-testid={`our-promise-item-${i}`}
          >
            <span className="text-[#C24933] font-bold text-lg sm:text-xl leading-none mt-0.5 shrink-0" aria-hidden="true">✗</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <p
        className="mt-8 sm:mt-10 max-w-2xl mx-auto text-sm sm:text-base text-[#7A7369] italic text-center"
        data-testid="our-promise-tagline"
      >
        Your notes belong to you. VidyaAI Study is the tool that makes them work harder — nothing more.
      </p>
    </section>
  );
}


// ------------------------------------------------------------------
// Pricing Section (Phase 56) — dual-mode (Student / Institute) toggle,
// dynamic seat counter for per-seat institute plans, Razorpay checkout
// binding via the existing `startRazorpaySubscription` helper.
// ------------------------------------------------------------------
// Catalogue keys MUST match RAZORPAY_PLAN_CATALOGUE in server.py:
//   selfstudy_plus_monthly        — ₹99/mo flat
//   selfstudy_pro_monthly         — ₹199/mo flat
//   institute_growth_perseat      — ₹30/student/mo  (per-seat → seats param required)
//   institute_enterprise_perseat  — ₹99/student/mo  (per-seat → seats param required)
//
// Anonymous visitors clicking a paid CTA get bounced to /register first; on
// completion they land back on /selfstudy/upgrade or /institute/upgrade which
// have their own (existing) checkout flows. We keep the homepage CTAs honest:
// signed-in users with the right role pay immediately, others see a friendly
// signup nudge instead of an opaque 403.
function PricingSection() {
  const { user, refresh } = useAuth();
  const [audience, setAudience] = React.useState("institute"); // "student" | "institute" — Institute tab is default (2026-07)

  return (
    <section
      id="pricing"
      className="container-x py-12 sm:py-16"
      data-testid="pricing-section"
    >
      <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
        <div className="label-eyebrow mb-4">Plans &amp; pricing</div>
        <h2 className="font-display text-3xl lg:text-4xl tracking-tight">
          Free <span className="text-[#C24933]">Shuru karo</span>. Upgrade jab Mann Kare.
        </h2>
        <p className="mt-3 text-base sm:text-lg text-[#C24933] font-medium">
          Roz ki ek Chai se bhi Sasta ☕
        </p>
      </div>

      {/* Audience toggle */}
      <div className="flex justify-center mb-10 sm:mb-12">
        <div
          className="inline-flex items-center gap-1 p-1 rounded-full border border-[#E6E2D8] bg-white shadow-sm"
          role="tablist"
          aria-label="Choose pricing audience"
          data-testid="pricing-audience-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={audience === "student"}
            onClick={() => setAudience("student")}
            className={`px-5 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
              audience === "student"
                ? "bg-[#1A1713] text-[#FDFBF7] shadow-sm"
                : "text-[#4A4640] hover:text-[#1A1713]"
            }`}
            data-testid="pricing-toggle-student"
          >
            For Self-Study Students
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={audience === "institute"}
            onClick={() => setAudience("institute")}
            className={`px-5 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
              audience === "institute"
                ? "bg-[#1A1713] text-[#FDFBF7] shadow-sm"
                : "text-[#4A4640] hover:text-[#1A1713]"
            }`}
            data-testid="pricing-toggle-institute"
          >
            For Coaching Institutes
          </button>
        </div>
      </div>

      {/* Cards */}
      {audience === "student" ? (
        <StudentPricingCards user={user} refresh={refresh} />
      ) : (
        <InstitutePricingCards user={user} refresh={refresh} />
      )}
    </section>
  );
}


// Friendly gate: signed-out visitors → /register, wrong-role users → toast.
function _guardCheckout(user, requiredRole, label) {
  if (!user) {
    toast.info(`Sign up first — takes 30 seconds — then jump straight to ${label}.`);
    window.location.assign(
      requiredRole === "self_study_student"
        ? "/register?role=self_study_student&next=/selfstudy/upgrade"
        : "/register?role=institute_admin&next=/institute/upgrade"
    );
    return false;
  }
  if (user.role !== requiredRole && user.role !== "super_admin") {
    toast.error(
      requiredRole === "self_study_student"
        ? "Self-study plans are only for self-study student accounts."
        : "Institute plans are only for institute admin accounts."
    );
    return false;
  }
  return true;
}


function StudentPricingCards({ user, refresh }) {
  const tiers = [
    {
      key: "free",
      label: "Free",
      tagline: "Start exploring — no card needed",
      priceInr: 0,
      cadence: "forever",
      caps: ["10 active documents", "20 AI questions / day", "100 page cap on PDFs", "Hinglish + 9 Indian languages"],
      cta: { type: "register", label: "Get Started Free", to: "/register?role=self_study_student" },
      accent: "#7A7369",
    },
    {
      key: "plus",
      label: "Plus",
      tagline: "For serious self-study learners",
      priceInr: 99,
      cadence: "month",
      caps: ["50 active documents · 200-page PDFs", "1 video lecture / month", "5 audio uploads / month", "100 AI questions / day"],
      cta: { type: "checkout", catalogue: "selfstudy_plus_monthly", label: "Subscribe to Plus" },
      accent: "#C24933",
      featured: true,
    },
    {
      key: "pro",
      label: "Pro",
      tagline: "Unlimited media — videos, audio, PDFs",
      priceInr: 199,
      cadence: "month",
      caps: ["100 active documents · 300-page PDFs", "10 video lectures / month", "20 audio uploads / month", "200 AI questions / day"],
      cta: { type: "checkout", catalogue: "selfstudy_pro_monthly", label: "Subscribe to Pro" },
      accent: "#3D5844",
    },
  ];

  return (
    <div className="grid md:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto" data-testid="student-pricing-cards">
      {tiers.map((t) => (
        <PriceCard
          key={t.key}
          tier={t}
          onPay={async () => {
            if (!_guardCheckout(user, "self_study_student", t.label)) return;
            await startRazorpaySubscription(t.cta.catalogue, {
              onSuccess: () => { refresh && refresh(); },
            });
          }}
        />
      ))}
    </div>
  );
}


function InstitutePricingCards({ user, refresh }) {
  const [growthSeats, setGrowthSeats] = React.useState(100);
  const GROWTH_PRICE = 30;

  return (
    <div className="grid md:grid-cols-2 gap-5 sm:gap-6 max-w-4xl mx-auto" data-testid="institute-pricing-cards">
      {/* Starter — free, no seat counter */}
      <PriceCard
        tier={{
          key: "starter",
          label: "Starter",
          tagline: "Free up to 50 students",
          priceInr: 0,
          cadence: "forever",
          caps: ["Up to 50 active students", "10 documents · 100-page PDFs", "Telegram + Web access", "10 AI questions per student per day"],
          cta: { type: "register", label: "Open Starter Account", to: "/register?role=institute_admin" },
          accent: "#7A7369",
        }}
        onPay={() => {
          if (!user) { window.location.assign("/register?role=institute_admin"); return; }
          toast.info("You're already on Starter — explore your institute dashboard.");
        }}
      />

      {/* Growth — per-seat with counter, no clamps */}
      <SeatPriceCard
        tier={{
          key: "growth",
          label: "Growth",
          tagline: "For scaling coaching centres",
          perSeatInr: GROWTH_PRICE,
          step: 10,
          caps: ["Students = your purchased seats (no ceiling)", "50 documents · 200-page PDFs", "10 videos · 25 audios / month", "50 AI questions per student per day"],
          accent: "#C24933",
          featured: true,
          ctaLabel: "Upgrade to Growth",
          catalogue: "institute_growth_perseat",
        }}
        seats={growthSeats}
        setSeats={setGrowthSeats}
        onPay={async () => {
          if (!_guardCheckout(user, "institute_admin", "Growth")) return;
          await startRazorpaySubscription("institute_growth_perseat", {
            seats: growthSeats,
            onSuccess: () => { refresh && refresh(); },
          });
        }}
      />

    </div>
  );
}


// Flat-price card used by Free / Plus / Pro / Starter
function PriceCard({ tier, onPay }) {
  const accent = tier.accent || "#1A1713";
  return (
    <div
      className={`card-surface flex flex-col !p-6 sm:!p-7 ${tier.featured ? "ring-2 ring-[#C24933] shadow-xl" : ""}`}
      data-testid={`pricing-card-${tier.key}`}
    >
      {tier.featured && (
        <div className="inline-block self-start px-3 py-1 rounded-full bg-[#C24933] text-[#FDFBF7] text-[10px] uppercase tracking-widest mb-3">
          Most popular
        </div>
      )}
      <div className="label-eyebrow mb-2" style={{ color: accent }}>{tier.label}</div>
      <div className="text-sm text-[#7A7369] mb-5">{tier.tagline}</div>
      <div className="font-display text-4xl sm:text-5xl text-[#1A1713] tracking-tight mb-1 flex items-baseline gap-1">
        <IndianRupee size={22} strokeWidth={2} className="opacity-70" />
        <span data-testid={`pricing-card-${tier.key}-price`}>{tier.priceInr}</span>
        <span className="text-base text-[#7A7369] font-normal">/{tier.cadence}</span>
      </div>
      <ul className="mt-6 space-y-3 text-sm text-[#4A4640] mb-7 flex-1">
        {tier.caps.map((c) => (
          <li key={c} className="flex gap-2"><Check size={14} className="shrink-0 mt-0.5 text-[#4A6B53]" /><span>{c}</span></li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onPay}
        className={tier.featured ? "btn-primary justify-center" : "btn-ghost justify-center"}
        data-testid={`pricing-card-${tier.key}-cta`}
      >
        {tier.cta.label} {tier.featured && <ArrowUpRight size={14} />}
      </button>
      {tier.priceInr > 0 && <UpiTrustBadge />}
    </div>
  );
}


// Per-seat card with +/- counter AND free-form numeric input — no rigid clamps.
// Phase 57 brief: "Strip away all rigid minimum boundaries, default configurations,
// or locked maximum ceilings." The only floor we keep is `seats ≥ 1` (you can't
// subscribe to 0 students), and a soft sanity ceiling of 100,000 to match
// `_DEFAULT_PLANS_CONFIG.institute_seat_cap` so callers can't accidentally type
// 9999999999 and overflow Razorpay's quantity int.
function SeatPriceCard({ tier, seats, setSeats, onPay }) {
  const monthly = seats * tier.perSeatInr;
  const HARD_FLOOR = 1;
  const HARD_CEIL = 100000;
  const step = tier.step || 10;
  const clamp = (v) => Math.max(HARD_FLOOR, Math.min(HARD_CEIL, v));
  const dec = () => setSeats(clamp(seats - step));
  const inc = () => setSeats(clamp(seats + step));
  const onInput = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw === "") { setSeats(HARD_FLOOR); return; }
    setSeats(clamp(parseInt(raw, 10)));
  };
  return (
    <div
      className={`card-surface flex flex-col !p-6 sm:!p-7 ${tier.featured ? "ring-2 ring-[#C24933] shadow-xl" : ""}`}
      data-testid={`pricing-card-${tier.key}`}
    >
      {tier.featured && (
        <div className="inline-block self-start px-3 py-1 rounded-full bg-[#C24933] text-[#FDFBF7] text-[10px] uppercase tracking-widest mb-3">
          Most popular
        </div>
      )}
      <div className="label-eyebrow mb-2" style={{ color: tier.accent }}>{tier.label}</div>
      <div className="text-sm text-[#7A7369] mb-5">{tier.tagline}</div>

      {/* Seat counter — free-form */}
      <div className="mb-5">
        <label htmlFor={`seat-input-${tier.key}`} className="text-[10px] uppercase tracking-widest font-semibold text-[#7A7369] mb-2 block">
          <Users size={12} className="inline -mt-0.5 mr-1.5" />
          Active students (any number)
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={dec}
            aria-label="Decrease students"
            className="w-9 h-9 rounded-full border border-[#E6E2D8] text-[#1A1713] grid place-items-center hover:bg-[#F4F1EB] transition-colors"
            data-testid={`pricing-card-${tier.key}-seat-dec`}
          >
            <Minus size={14} strokeWidth={2} />
          </button>
          <input
            id={`seat-input-${tier.key}`}
            type="number"
            inputMode="numeric"
            min={HARD_FLOOR}
            max={HARD_CEIL}
            value={seats}
            onChange={onInput}
            className="flex-1 text-center font-display text-2xl text-[#1A1713] tabular-nums tracking-tight bg-transparent border-b-2 border-[#E6E2D8] focus:border-[#C24933] outline-none py-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            data-testid={`pricing-card-${tier.key}-seat-count`}
            aria-label={`${tier.label} seat count`}
          />
          <button
            type="button"
            onClick={inc}
            aria-label="Increase students"
            className="w-9 h-9 rounded-full border border-[#E6E2D8] text-[#1A1713] grid place-items-center hover:bg-[#F4F1EB] transition-colors"
            data-testid={`pricing-card-${tier.key}-seat-inc`}
          >
            <Plus size={14} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Dynamic total */}
      <div className="font-display text-3xl sm:text-4xl text-[#1A1713] tracking-tight mb-1 flex items-baseline gap-1">
        <IndianRupee size={20} strokeWidth={2} className="opacity-70" />
        <span data-testid={`pricing-card-${tier.key}-total`}>{monthly.toLocaleString("en-IN")}</span>
        <span className="text-sm text-[#7A7369] font-normal">/month</span>
      </div>
      <div className="text-xs text-[#7A7369]">
        ₹{tier.perSeatInr} × {seats.toLocaleString("en-IN")} students
      </div>

      <ul className="mt-6 space-y-3 text-sm text-[#4A4640] mb-7 flex-1">
        {tier.caps.map((c) => (
          <li key={c} className="flex gap-2"><Check size={14} className="shrink-0 mt-0.5 text-[#4A6B53]" /><span>{c}</span></li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onPay}
        className={tier.featured ? "btn-primary justify-center" : "btn-ghost justify-center"}
        data-testid={`pricing-card-${tier.key}-cta`}
      >
        {tier.ctaLabel} <ArrowUpRight size={14} />
      </button>
      <UpiTrustBadge />
    </div>
  );
}


// Indian payments trust badge — rendered under every paid CTA.
function UpiTrustBadge() {
  return (
    <div
      className="mt-3 flex items-start gap-2 text-[11px] text-[#4A4640] leading-snug"
      data-testid="upi-trust-badge"
    >
      <CreditCard size={12} className="shrink-0 mt-0.5 text-[#229ED9]" />
      <span>
        💳 Supports all Indian UPI apps (PhonePe, GPay, Paytm), Cards, and Netbanking.
      </span>
    </div>
  );
}

